import { faClone, faPen, faPlus, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, IconButton, Tooltip } from '@mui/material';
import { useContext, useEffect, useState } from 'react';

import { PortfolioSidebar } from '../../../../components/portfolio-sidebar';
import { BacktestingContext } from '../../../../providers/backtesting/backtesting-provider';
import { STRATEGY_TEMPLATES } from '../../../../providers/backtesting/constants/strategy-templates';
import Utils from '../../../../utils/immersive_utils';
import {
  BacktestTabModalView,
  HandleBacktestTabModal,
} from '../backtest-tabs/handle-backtest-tab-modal';

import './styles.scss';

const MAX_LABEL_LENGTH = 40;

interface BacktestSidebarProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (value: boolean) => void;
}

// The persistent studies rail. Top: "+ New study" (a blank draft with one
// backtest, straight into the shell). Then a fixed "Model backtests" section
// (the pre-canned templates — click to seed a new draft from one) above the
// "Saved backtests" library (the persisted saved configs). The current working
// copy shows as an unsaved draft until it's saved/run. There is no separate
// landing page — the shell is the only content surface.
//
// Library entries auto-save on run (no explicit Save needed) and support
// rename (save-as-new + delete-old) and duplicate (save-as-copy) inline.
export const BacktestSidebar = ({ isSidebarOpen, setIsSidebarOpen }: BacktestSidebarProps) => {
  const {
    savedConfigs,
    listSavedConfigs,
    loadSavedConfig,
    deleteSavedConfig,
    renameSavedConfig,
    duplicateSavedConfig,
    activeSavedConfigName,
    startNewBacktest,
    getActiveBacktest,
    isDeletingSavedConfig,
  } = useContext(BacktestingContext);

  // One modal, three uses (delete / rename / duplicate), scoped to a target
  // library entry. Null = closed.
  const [modal, setModal] = useState<{ view: BacktestTabModalView; name: string } | null>(null);

  useEffect(() => {
    listSavedConfigs();
  }, [listSavedConfigs]);

  const activeBacktest = getActiveBacktest();
  const isDraft = activeSavedConfigName === null;
  const draftLabel = activeBacktest?.label || 'Untitled study';
  const configs = savedConfigs ?? [];

  return (
    <PortfolioSidebar
      isPortfolioSidebarOpen={isSidebarOpen}
      setIsPortfolioSidebarOpen={setIsSidebarOpen}
      title="Studies"
      resizable
    >
      <div className="backtest-sidebar">
        <Button className="backtest-sidebar-add" onClick={() => startNewBacktest()}>
          <FontAwesomeIcon icon={faPlus} />
          <span>New study</span>
        </Button>

        <div className="backtest-sidebar-list">
          {isDraft && (
            <>
              <span className="backtest-sidebar-section-label">Working draft</span>
              <div className="backtest-sidebar-item active">
                <span className="backtest-sidebar-item-label">
                  {Utils.truncateLabel(draftLabel, MAX_LABEL_LENGTH)}
                </span>
                <span className="backtest-sidebar-item-draft">Draft</span>
              </div>
            </>
          )}

          <span className="backtest-sidebar-section-label">Model backtests</span>
          {STRATEGY_TEMPLATES.map((template) => (
            <div
              key={template.id}
              className="backtest-sidebar-item"
              onClick={() => startNewBacktest(template)}
            >
              <span className="backtest-sidebar-item-label">
                {Utils.truncateLabel(template.label, MAX_LABEL_LENGTH)}
              </span>
              <span className={`backtest-sidebar-item-sentiment ${template.sentiment}`}>
                {template.sentiment}
              </span>
            </div>
          ))}

          <span className="backtest-sidebar-section-label">Saved backtests</span>
          {configs.length === 0 ? (
            <span className="backtest-sidebar-empty">No saved backtests yet.</span>
          ) : (
            configs.map((config) => {
              const isActive = config.name === activeSavedConfigName;
              return (
                <Tooltip
                  key={config.name}
                  title={config.name.length >= MAX_LABEL_LENGTH ? config.name : ''}
                >
                  <div
                    className={`backtest-sidebar-item ${isActive ? 'active' : ''}`}
                    onClick={() => loadSavedConfig(config.name)}
                  >
                    <span className="backtest-sidebar-item-label">
                      {Utils.truncateLabel(config.name, MAX_LABEL_LENGTH)}
                    </span>
                    <span className="backtest-sidebar-item-actions">
                      <IconButton
                        className="backtest-sidebar-item-action"
                        onClick={(event) => {
                          event.stopPropagation();
                          setModal({ view: BacktestTabModalView.RENAME, name: config.name });
                        }}
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </IconButton>
                      <IconButton
                        className="backtest-sidebar-item-action"
                        onClick={(event) => {
                          event.stopPropagation();
                          setModal({ view: BacktestTabModalView.DUPLICATE, name: config.name });
                        }}
                      >
                        <FontAwesomeIcon icon={faClone} />
                      </IconButton>
                      <IconButton
                        className="backtest-sidebar-item-delete"
                        disabled={isDeletingSavedConfig}
                        onClick={(event) => {
                          event.stopPropagation();
                          setModal({ view: BacktestTabModalView.DELETE, name: config.name });
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </IconButton>
                    </span>
                  </div>
                </Tooltip>
              );
            })
          )}
        </div>
      </div>

      <HandleBacktestTabModal
        open={modal !== null}
        view={modal?.view ?? BacktestTabModalView.DELETE}
        handleClose={() => setModal(null)}
        onDelete={() => {
          if (modal) deleteSavedConfig(modal.name);
          setModal(null);
        }}
        onSave={() => {}}
        onRename={(newName) => {
          if (modal) renameSavedConfig(modal.name, newName);
          setModal(null);
        }}
        onDuplicate={(newName) => {
          if (modal) duplicateSavedConfig(modal.name, newName);
          setModal(null);
        }}
      />
    </PortfolioSidebar>
  );
};
